import React from "react";
import { Slider } from "./Slider";

export interface RangeSliderProps {
  /** Minimum value of the range (e.g. 0) */
  min: number;
  /** Maximum value of the range (e.g. 1) */
  max: number;
  /** Step increment */
  step?: number;
  /** Current values [minValue, maxValue] */
  value: [number, number];
  /** Callback when value changes */
  onValueChange: (value: [number, number]) => void;
  /** Optional label */
  label?: string;
  /** Optional className for the container */
  className?: string;
  /** Deprecated (DaisyUI sizing) — kept for back-compat, no longer used. */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Deprecated (DaisyUI colour) — kept for back-compat, no longer used. */
  color?: "primary" | "secondary" | "accent" | "neutral" | "success" | "warning" | "info" | "error";
}

/**
 * Dual-handle range slider (016 redesign) built on the token-based Radix `Slider`.
 * Replaces the previous stacked DaisyUI `range` inputs; the public API is unchanged.
 */
export function RangeSlider({
  min,
  max,
  step = 0.01,
  value: [minVal, maxVal],
  onValueChange,
  label,
  className = "",
}: RangeSliderProps) {
  return (
    <div className={`w-full ${className}`.trim()}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      )}
      <Slider
        min={min}
        max={max}
        step={step}
        value={[minVal, maxVal]}
        onValueChange={(v) => onValueChange([v[0], v[1]])}
        aria-label={label ?? "Range"}
      />
      <div className="flex justify-between text-xs text-muted-foreground mt-2">
        <span>{minVal.toFixed(2)}</span>
        <span>{maxVal.toFixed(2)}</span>
      </div>
    </div>
  );
}
